import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../context/WalletContextV2';
import { getTokens, getSwapQuote, executeSwap, getTradeHistory } from '../services/api';
import { web3Service, CONTRACT_ADDRESSES, BLOKISTA_NETWORK, ERC20_ABI, BCUSD_USD_PRICE, WBCC_USD_PRICE_FALLBACK } from '../services/web3';
import TokenSelector from '../components/TokenSelector';
import TradeChart from '../components/TradeChart';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../components/ui/tooltip';
import {
  ArrowDownUp,
  Settings,
  Info,
  ChevronDown,
  AlertTriangle,
  Check,
  Loader2,
  TrendingUp,
  History
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Deeply unwrap a JSON-RPC / ethers error to find the real revert reason.
const extractRpcError = (err) => {
  if (!err) return 'Unknown error';
  let parsedStack = null;
  try {
    if (typeof err.stack === 'string' && err.stack.trim().startsWith('{')) {
      parsedStack = JSON.parse(err.stack);
    }
  } catch {
    /* ignore */
  }
  const candidates = [
    err?.reason,
    err?.error?.error?.data?.originalError?.message,
    err?.error?.error?.data?.message,
    err?.error?.error?.message,
    err?.error?.data?.originalError?.message,
    err?.error?.data?.message,
    err?.error?.message,
    err?.data?.originalError?.message,
    err?.data?.message,
    parsedStack?.data?.originalError?.message,
    parsedStack?.data?.message,
    parsedStack?.message,
    err?.message,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim() && !c.toLowerCase().includes('internal json-rpc')) {
      return c.replace(/^execution reverted:?\s*/i, '').trim();
    }
  }
  return err.message || 'Transaction failed';
};

const SwapPage = () => {
  const { isConnected, connectWallet, isConnecting, address, isCorrectNetwork, switchNetwork, loadBalances } = useWallet();
  
  const [tokens, setTokens] = useState([]);
  const [sellToken, setSellToken] = useState(null);
  const [buyToken, setBuyToken] = useState(null);
  const [sellAmount, setSellAmount] = useState('');
  const [buyAmount, setBuyAmount] = useState('');
  const [showSellSelector, setShowSellSelector] = useState(false);
  const [showBuySelector, setShowBuySelector] = useState(false);
  const [slippage, setSlippage] = useState(0.5);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapSuccess, setSwapSuccess] = useState(false);
  const [priceImpact, setPriceImpact] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(0);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [activeView, setActiveView] = useState('swap');
  const [recentTrades, setRecentTrades] = useState([]);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [txHash, setTxHash] = useState(null);
  const [sellBalance, setSellBalance] = useState(0);
  const [buyBalance, setBuyBalance] = useState(0);
  const [sellUsdPrice, setSellUsdPrice] = useState(0);
  const [buyUsdPrice, setBuyUsdPrice] = useState(0);

  // Derive a token's USD price from on-chain pool reserves.
  // 1. BCUSD itself = 1
  // 2. Direct (token, BCUSD) pool → ratio
  // 3. Route through (token, WBCC) → (WBCC, BCUSD)
  const computeUsdPrice = useCallback(async (token) => {
    if (!token) return 0;
    try {
      const rpc = new ethers.providers.JsonRpcProvider(BLOKISTA_NETWORK.rpcUrls[0]);
      const factoryAbi = ['function getPair(address,address) view returns (address)'];
      const pairAbi = [
        'function getReserves() view returns (uint112,uint112,uint32)',
        'function token0() view returns (address)',
      ];
      const factoryC = new ethers.Contract(CONTRACT_ADDRESSES.FACTORY, factoryAbi, rpc);

      const tokenAddr = (token.isNative ? CONTRACT_ADDRESSES.WBCC : token.address).toLowerCase();
      const bcusd = CONTRACT_ADDRESSES.BCUSD.toLowerCase();
      const wbcc = CONTRACT_ADDRESSES.WBCC.toLowerCase();
      const tokenDecimals = token.decimals || 18;

      // BCUSD itself
      if (tokenAddr === bcusd) return BCUSD_USD_PRICE;

      // Helper: get reserves of (a, b) pair. Returns { reserveA, reserveB } or null.
      const reservesOf = async (a, b, decA, decB) => {
        const pair = await factoryC.getPair(a, b);
        if (!pair || pair === ethers.constants.AddressZero) return null;
        const pairC = new ethers.Contract(pair, pairAbi, rpc);
        const [r, t0] = await Promise.all([pairC.getReserves(), pairC.token0()]);
        const t0Lower = t0.toLowerCase();
        const aIsToken0 = t0Lower === a.toLowerCase();
        const reserveARaw = aIsToken0 ? r[0] : r[1];
        const reserveBRaw = aIsToken0 ? r[1] : r[0];
        if (reserveARaw.isZero() || reserveBRaw.isZero()) return null;
        return {
          reserveA: parseFloat(ethers.utils.formatUnits(reserveARaw, decA)),
          reserveB: parseFloat(ethers.utils.formatUnits(reserveBRaw, decB)),
        };
      };

      // Step 1: direct token <-> BCUSD pool. Price in USD = (BCUSD per token) * BCUSD_USD_PRICE
      const direct = await reservesOf(tokenAddr, bcusd, tokenDecimals, 18);
      if (direct) return (direct.reserveB / direct.reserveA) * BCUSD_USD_PRICE;

      // Step 2: route token -> WBCC -> BCUSD (or fallback WBCC price)
      const tokToWbcc = await reservesOf(tokenAddr, wbcc, tokenDecimals, 18);
      if (!tokToWbcc) return 0;
      const wbccToBcusd = await reservesOf(wbcc, bcusd, 18, 18);
      const wbccInUsd = wbccToBcusd
        ? (wbccToBcusd.reserveB / wbccToBcusd.reserveA) * BCUSD_USD_PRICE
        : WBCC_USD_PRICE_FALLBACK;
      const tokenInWbcc = tokToWbcc.reserveB / tokToWbcc.reserveA;
      return tokenInWbcc * wbccInUsd;
    } catch (err) {
      console.error(`[SwapPage] USD price lookup failed for ${token.symbol}:`, err);
      return 0;
    }
  }, []);

  // Refresh USD prices whenever the selected tokens change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [sp, bp] = await Promise.all([
        computeUsdPrice(sellToken),
        computeUsdPrice(buyToken),
      ]);
      if (!cancelled) {
        setSellUsdPrice(sp);
        setBuyUsdPrice(bp);
      }
    })();
    return () => { cancelled = true; };
  }, [sellToken, buyToken, computeUsdPrice]);

  useEffect(() => {
    const loadTokens = async () => {
      try {
        const fetchedTokens = await getTokens();
        setTokens(fetchedTokens);
        if (fetchedTokens.length >= 3) {
          setSellToken(fetchedTokens[0]);
          setBuyToken(fetchedTokens[2]);
        }
      } catch (error) {
        console.error('Error loading tokens:', error);
      }
    };
    loadTokens();
  }, []);

  useEffect(() => {
    if (sellToken && buyToken) {
      const loadTrades = async () => {
        try {
          const trades = await getTradeHistory(sellToken.address, buyToken.address, 20);
          if (trades.length > 0) {
            const formattedTrades = trades.map((trade, index) => ({
              id: trade.id || index,
              type: trade.token0Amount > 0 ? 'sell' : 'buy',
              amount: trade.token0Amount.toFixed(4),
              price: trade.price.toFixed(6),
              total: (trade.token0Amount * trade.price).toFixed(2),
              time: trade.timestamp.toLocaleTimeString(),
              txHash: trade.txHash,
              isReal: true
            }));
            setRecentTrades(formattedTrades);
          } else {
            setRecentTrades([]);
          }
        } catch (error) {
          console.error('Error loading trade history:', error);
          setRecentTrades([]);
        }
      };
      loadTrades();
    }
  }, [sellToken, buyToken]);

  const fetchQuote = useCallback(async () => {
    if (!sellToken || !buyToken || !sellAmount || parseFloat(sellAmount) <= 0) {
      setBuyAmount('');
      setPriceImpact(0);
      setExchangeRate(0);
      return;
    }
    setIsLoadingQuote(true);
    try {
      // Compute the quote from on-chain reserves via Router.getAmountsOut.
      // This is the only correct source of truth for pricing.
      const rpc = new ethers.providers.JsonRpcProvider(BLOKISTA_NETWORK.rpcUrls[0]);
      const routerAbi = ['function getAmountsOut(uint256,address[]) view returns (uint256[])'];
      const router = new ethers.Contract(CONTRACT_ADDRESSES.ROUTER, routerAbi, rpc);

      const sellAddr = sellToken.isNative ? CONTRACT_ADDRESSES.WBCC : sellToken.address;
      const buyAddr = buyToken.isNative ? CONTRACT_ADDRESSES.WBCC : buyToken.address;
      const sellDecimals = sellToken.decimals || 18;
      const buyDecimals = buyToken.decimals || 18;

      const amountInWei = ethers.utils.parseUnits(sellAmount, sellDecimals);
      const amounts = await router.getAmountsOut(amountInWei, [sellAddr, buyAddr]);
      const amountOut = parseFloat(ethers.utils.formatUnits(amounts[amounts.length - 1], buyDecimals));

      setBuyAmount(amountOut.toFixed(6));
      const rate = amountOut / parseFloat(sellAmount);
      setExchangeRate(rate);
      setPriceImpact(0); // V2 doesn't expose this directly; ok to leave 0 for now
    } catch (error) {
      console.error('Error fetching on-chain quote (likely no liquidity yet):', error);
      // Try backend as fallback (uses DB prices when set, or 1:1 if not)
      try {
        const quote = await getSwapQuote(sellToken.address, buyToken.address, parseFloat(sellAmount));
        setBuyAmount(quote.amountOut.toString());
        setPriceImpact(quote.priceImpact);
        setExchangeRate(quote.exchangeRate);
      } catch {
        setBuyAmount('');
        setExchangeRate(0);
        setPriceImpact(0);
      }
    }
    setIsLoadingQuote(false);
  }, [sellToken, buyToken, sellAmount]);

  useEffect(() => {
    const debounceTimer = setTimeout(fetchQuote, 300);
    return () => clearTimeout(debounceTimer);
  }, [fetchQuote]);

  const handleSwapTokens = () => {
    const tempToken = sellToken;
    const tempAmount = sellAmount;
    setSellToken(buyToken);
    setBuyToken(tempToken);
    setSellAmount(buyAmount);
    setBuyAmount(tempAmount);
  };

  // Fetch on-chain balance for a single token (native-aware).
  // Uses a dedicated Blokista RPC provider so reads are independent of whatever
  // network MetaMask is currently pointed at.
  const fetchTokenBalance = useCallback(async (token, walletAddress) => {
    if (!token || !walletAddress) return 0;
    const rpc = new ethers.providers.JsonRpcProvider(BLOKISTA_NETWORK.rpcUrls[0]);
    try {
      if (token.isNative || token.address === '0x0000000000000000000000000000000000000000') {
        const raw = await rpc.getBalance(walletAddress);
        const val = parseFloat(ethers.utils.formatEther(raw)) || 0;
        console.debug(`[SwapPage] Native balance for ${walletAddress}:`, val);
        return val;
      }
      const contract = new ethers.Contract(token.address, ERC20_ABI, rpc);
      const [raw, decimals] = await Promise.all([
        contract.balanceOf(walletAddress),
        Promise.resolve(token.decimals || 18),
      ]);
      const val = parseFloat(ethers.utils.formatUnits(raw, decimals)) || 0;
      console.debug(`[SwapPage] ${token.symbol} balance for ${walletAddress}:`, val);
      return val;
    } catch (err) {
      console.error(`[SwapPage] Balance fetch failed for ${token.symbol} (${token.address}):`, err);
      return 0;
    }
  }, []);

  // Refresh balances for the currently selected sell & buy tokens
  const refreshBalances = useCallback(async () => {
    if (!isConnected || !address) {
      setSellBalance(0);
      setBuyBalance(0);
      return;
    }
    const [sb, bb] = await Promise.all([
      fetchTokenBalance(sellToken, address),
      fetchTokenBalance(buyToken, address),
    ]);
    setSellBalance(sb);
    setBuyBalance(bb);

    // Keep global balances in sync for header / other pages
    if (loadBalances && (sellToken || buyToken)) {
      const list = [sellToken, buyToken]
        .filter(Boolean)
        .map((t) => ({
          id: t.id,
          address: t.address,
          isNative: t.isNative,
          decimals: t.decimals,
        }));
      loadBalances(address, list);
    }
  }, [isConnected, address, sellToken, buyToken, fetchTokenBalance, loadBalances]);

  useEffect(() => {
    refreshBalances();
  }, [refreshBalances]);

  const handleMaxClick = () => {
    if (isConnected && sellToken) {
      setSellAmount(sellBalance.toString());
    }
  };

  const checkApproval = useCallback(async () => {
    if (!isConnected || !sellToken || !sellAmount || parseFloat(sellAmount) <= 0) {
      return false;
    }
    if (sellToken.isNative || sellToken.address === '0x0000000000000000000000000000000000000000') {
      return false;
    }
    try {
      const allowance = await web3Service.checkAllowance(sellToken.address, CONTRACT_ADDRESSES.ROUTER, address);
      const sellAmountNum = parseFloat(sellAmount);
      return parseFloat(allowance) < sellAmountNum;
    } catch (error) {
      console.error('Error checking allowance:', error);
      return true;
    }
  }, [isConnected, sellToken, sellAmount, address]);

  useEffect(() => {
    let isMounted = true;
    const check = async () => {
      const needs = await checkApproval();
      if (isMounted) setNeedsApproval(needs);
    };
    check();
    return () => { isMounted = false; };
  }, [checkApproval]);

  const handleApprove = async () => {
    if (!sellToken) return;
    setIsApproving(true);
    try {
      await web3Service.approveTokenMax(sellToken.address, CONTRACT_ADDRESSES.ROUTER);
      setNeedsApproval(false);
    } catch (error) {
      console.error('Approval failed:', error);
      alert('Approval failed: ' + extractRpcError(error));
    }
    setIsApproving(false);
  };

  const handleSwap = async () => {
    if (!isConnected || !sellAmount || parseFloat(sellAmount) <= 0) return;
    setIsSwapping(true);
    setTxHash(null);
    try {
      const sellAddr = sellToken.isNative ? CONTRACT_ADDRESSES.WBCC : sellToken.address;
      const buyAddr = buyToken.isNative ? CONTRACT_ADDRESSES.WBCC : buyToken.address;
      const sellDecimals = sellToken.decimals || 18;
      const buyDecimals = buyToken.decimals || 18;
      const amountIn = ethers.utils.parseUnits(sellAmount, sellDecimals);
      const minAmountOut = ethers.utils.parseUnits((parseFloat(buyAmount) * (1 - slippage / 100)).toFixed(buyDecimals), buyDecimals);

      // Pre-flight: verify the pair exists AND has liquidity. Saves gas + surfaces a
      // clean error before MetaMask pops up.
      const rpc = new ethers.providers.JsonRpcProvider(BLOKISTA_NETWORK.rpcUrls[0]);
      const factoryAbi = ['function getPair(address,address) view returns (address)'];
      const pairAbi = ['function getReserves() view returns (uint112,uint112,uint32)'];
      const factoryC = new ethers.Contract(CONTRACT_ADDRESSES.FACTORY, factoryAbi, rpc);
      const pairAddr = await factoryC.getPair(sellAddr, buyAddr);
      if (!pairAddr || pairAddr === ethers.constants.AddressZero) {
        throw new Error(`No liquidity pool exists for ${sellToken.symbol}/${buyToken.symbol}. Create one from the Pools page first.`);
      }
      const pairC = new ethers.Contract(pairAddr, pairAbi, rpc);
      const [r0, r1] = await pairC.getReserves();
      if (r0.isZero() || r1.isZero()) {
        throw new Error(`${sellToken.symbol}/${buyToken.symbol} pool has no liquidity. Add liquidity from the Pools page first before swapping.`);
      }

      let result;
      if (sellToken.isNative) {
        result = await web3Service.swapExactETHForTokens(minAmountOut, [CONTRACT_ADDRESSES.WBCC, buyAddr], amountIn);
      } else if (buyToken.isNative) {
        result = await web3Service.swapExactTokensForETH(amountIn, minAmountOut, [sellAddr, CONTRACT_ADDRESSES.WBCC]);
      } else {
        const path = [sellAddr, buyAddr];
        result = await web3Service.swapExactTokensForTokens(amountIn, minAmountOut, path);
      }
      setTxHash(result.hash);
      setSwapSuccess(true);
      await executeSwap(address, sellToken.address, buyToken.address, parseFloat(sellAmount), parseFloat(buyAmount), slippage, result.hash);
      refreshBalances();
      setTimeout(() => {
        setSwapSuccess(false);
        setSellAmount('');
        setBuyAmount('');
        setTxHash(null);
      }, 5000);
    } catch (error) {
      console.error('Swap failed (full error):', error);
      alert('Swap failed: ' + extractRpcError(error));
    }
    setIsSwapping(false);
  };

  const insufficientBalance = parseFloat(sellAmount) > sellBalance;

  if (tokens.length === 0) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[#0d0d0d] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-yellow-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0d0d0d] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-yellow-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-1/4 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 pt-8 pb-20">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4 order-2 lg:order-1 min-w-0 overflow-hidden">
            <TradeChart token0={sellToken} token1={buyToken} height={350} />
            <Card className="bg-[#1a1a1a] border-white/5 p-4 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <History className="w-5 h-5 text-yellow-400" />
                  <h3 className="font-semibold text-white">Recent Trades</h3>
                </div>
                {recentTrades.length > 0 && recentTrades[0].isReal && (
                  <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">Live</span>
                )}
              </div>
              {recentTrades.length === 0 ? (
                <div className="py-8 text-center">
                  <History className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-400 text-sm">No trades yet for this pair</p>
                  <p className="text-gray-500 text-xs mt-1">Make the first trade to see history here!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-400 border-b border-white/5">
                        <th className="text-left py-2">Price ({buyToken?.symbol})</th>
                        <th className="text-right py-2">Amount ({sellToken?.symbol})</th>
                        <th className="text-right py-2">Total</th>
                        <th className="text-right py-2">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentTrades.map((trade) => (
                        <tr key={trade.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                          onClick={() => trade.txHash && window.open(`https://bccscan.com/tx/${trade.txHash}`, '_blank')}>
                          <td className={`py-2 ${trade.type === 'buy' ? 'text-green-400' : 'text-red-400'}`}>{trade.price}</td>
                          <td className="text-right text-white">{trade.amount}</td>
                          <td className="text-right text-gray-400">{trade.total}</td>
                          <td className="text-right text-gray-500">{trade.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          <div className="space-y-4 order-1 lg:order-2 min-w-0">
            <div className="flex items-center justify-between">
              <Tabs value={activeView} onValueChange={setActiveView} className="w-auto">
                <TabsList className="bg-white/5 border border-white/10 rounded-xl p-1">
                  <TabsTrigger value="swap" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black px-4">Swap</TabsTrigger>
                  <TabsTrigger value="limit" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black px-4">Limit</TabsTrigger>
                </TabsList>
              </Tabs>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-all">
                    <Settings className="w-5 h-5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 bg-[#1a1a1a] border-white/10 text-white" align="end">
                  <div className="space-y-4">
                    <h3 className="font-semibold">Swap Settings</h3>
                    <div>
                      <label className="text-sm text-gray-400 mb-2 block">Slippage Tolerance</label>
                      <div className="flex gap-2">
                        {[0.1, 0.5, 1.0].map((value) => (
                          <button key={value} onClick={() => setSlippage(value)}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${slippage === value ? 'bg-yellow-500 text-black' : 'bg-white/5 text-white hover:bg-white/10'}`}>
                            {value}%
                          </button>
                        ))}
                        <div className="flex-1 relative">
                          <input type="number" value={slippage} onChange={(e) => setSlippage(parseFloat(e.target.value) || 0)}
                            className="w-full py-2 px-3 rounded-lg bg-white/5 text-white text-sm text-right pr-6 focus:outline-none focus:ring-1 focus:ring-yellow-500" />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <Card className="bg-[#1a1a1a] border-white/5 p-4 rounded-2xl">
              <div className="bg-white/5 rounded-xl p-4 mb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Sell</span>
                  {isConnected && (
                    <button onClick={handleMaxClick} className="text-sm text-gray-400 hover:text-yellow-400 transition-colors">
                      Balance: {sellBalance.toFixed(4)}<span className="ml-1 text-yellow-400">MAX</span>
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <input type="number" value={sellAmount} onChange={(e) => setSellAmount(e.target.value)} placeholder="0"
                    className="flex-1 min-w-0 bg-transparent text-3xl font-medium text-white placeholder:text-gray-600 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                  <button onClick={() => setShowSellSelector(true)} className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 transition-all">
                    {sellToken ? (<><img src={sellToken.logo} alt={sellToken.symbol} className="w-6 h-6 rounded-full" /><span className="font-semibold text-white">{sellToken.symbol}</span>{sellToken.verified && <VerifiedBadge />}</>) : (<span className="text-gray-400">Select</span>)}
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                {sellAmount && sellToken && sellUsdPrice > 0 && (
                  <div className="mt-2 text-sm text-gray-500">
                    ~${(parseFloat(sellAmount) * sellUsdPrice).toFixed(2)}
                  </div>
                )}
              </div>

              <div className="relative h-0 flex justify-center">
                <button onClick={handleSwapTokens} className="absolute -top-4 z-10 p-2 rounded-xl bg-[#1a1a1a] border-4 border-[#0d0d0d] text-gray-400 hover:text-yellow-400 hover:rotate-180 transition-all duration-300">
                  <ArrowDownUp className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-white/5 rounded-xl p-4 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Buy</span>
                  {isConnected && <span className="text-sm text-gray-500">Balance: {buyBalance.toFixed(4)}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0 flex items-center">
                    {isLoadingQuote ? (<Loader2 className="w-6 h-6 text-gray-500 animate-spin" />) : (
                      <input type="number" value={buyAmount} readOnly placeholder="0"
                        className="w-full bg-transparent text-3xl font-medium text-white placeholder:text-gray-600 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                    )}
                  </div>
                  <button onClick={() => setShowBuySelector(true)} className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 transition-all">
                    {buyToken ? (<><img src={buyToken.logo} alt={buyToken.symbol} className="w-6 h-6 rounded-full" /><span className="font-semibold text-white">{buyToken.symbol}</span>{buyToken.verified && <VerifiedBadge />}</>) : (<span className="text-gray-400">Select</span>)}
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                {buyAmount && buyUsdPrice > 0 && (
                  <div className="mt-2 text-sm text-gray-500">
                    ~${(parseFloat(buyAmount) * buyUsdPrice).toFixed(2)}
                  </div>
                )}
              </div>

              {sellAmount && buyAmount && (
                <div className="mt-4 p-3 rounded-xl bg-white/5 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Rate</span>
                    <span className="text-white">1 {sellToken?.symbol} = {exchangeRate.toFixed(6)} {buyToken?.symbol}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400 flex items-center gap-1">
                      Price Impact
                      <TooltipProvider><Tooltip><TooltipTrigger><Info className="w-3 h-3" /></TooltipTrigger>
                        <TooltipContent className="bg-[#2a2a2a] border-white/10"><p className="text-xs">The difference between market price and estimated price</p></TooltipContent>
                      </Tooltip></TooltipProvider>
                    </span>
                    <span className={priceImpact > 1 ? 'text-yellow-400' : 'text-green-400'}>{priceImpact}%</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Max Slippage</span>
                    <span className="text-white">{slippage}%</span>
                  </div>
                </div>
              )}

              <div className="mt-4">
                {!isConnected ? (
                  <Button onClick={connectWallet} disabled={isConnecting} className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black rounded-xl">
                    {isConnecting ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Connecting...</>) : ('Connect Wallet')}
                  </Button>
                ) : !isCorrectNetwork ? (
                  <Button onClick={switchNetwork} className="w-full py-6 text-lg font-semibold bg-yellow-500/20 text-yellow-400 rounded-xl">
                    <AlertTriangle className="w-5 h-5 mr-2" />Switch to Blokista Network
                  </Button>
                ) : !sellAmount || parseFloat(sellAmount) <= 0 ? (
                  <Button disabled className="w-full py-6 text-lg font-semibold bg-white/10 text-gray-500 rounded-xl cursor-not-allowed">Enter an amount</Button>
                ) : insufficientBalance ? (
                  <Button disabled className="w-full py-6 text-lg font-semibold bg-red-500/20 text-red-400 rounded-xl cursor-not-allowed">
                    <AlertTriangle className="w-5 h-5 mr-2" />Insufficient {sellToken?.symbol} balance
                  </Button>
                ) : needsApproval ? (
                  <Button onClick={handleApprove} disabled={isApproving} className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black rounded-xl">
                    {isApproving ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Approving...</>) : (<>Approve {sellToken?.symbol}</>)}
                  </Button>
                ) : swapSuccess ? (
                  <div className="space-y-2">
                    <Button disabled className="w-full py-6 text-lg font-semibold bg-green-500 text-white rounded-xl"><Check className="w-5 h-5 mr-2" />Swap Successful!</Button>
                    {txHash && (<a href={`https://bccscan.com/tx/${txHash}`} target="_blank" rel="noopener noreferrer" className="block text-center text-sm text-yellow-400 hover:text-amber-300">View on Explorer →</a>)}
                  </div>
                ) : (
                  <Button onClick={handleSwap} disabled={isSwapping} className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black rounded-xl">
                    {isSwapping ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Swapping...</>) : ('Swap')}
                  </Button>
                )}
              </div>
            </Card>
            <p className="text-center text-gray-500 text-sm">Trade tokens with zero app fees on Blokista network</p>
          </div>
        </div>
      </div>

      <TokenSelector open={showSellSelector} onOpenChange={setShowSellSelector} onSelect={setSellToken} selectedToken={sellToken} excludeToken={buyToken} tokens={tokens} />
      <TokenSelector open={showBuySelector} onOpenChange={setShowBuySelector} onSelect={setBuyToken} selectedToken={buyToken} excludeToken={sellToken} tokens={tokens} />
    </div>
  );
};

export default SwapPage;
